import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import * as Dialog from 'primitives/dialog'
import * as React from 'react'
import { callAll } from 'utils/call-all'
import { useLangCookie } from 'cf-i18n/cookies'
import { defaultLanguage } from 'cf-i18n/constants'

import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import { Button } from './button'
import { Confetti } from './confetti'
import i18n from '../i18n/complete-screens-i18n.json'

export function UpgradeCompleteScreenDialog({
  subscriptionType = 'all-access',
}: {
  subscriptionType: 'all-access' | 'studio-ai'
}) {
  const { onOpenChange } = Dialog.useDialog()
  const prefersReducedMotion = useReducedMotion()

  const onClose = React.useCallback(
    function onClose() {
      onOpenChange(false)
    },
    [onOpenChange],
  )

  return (
    <div className="relative h-full max-h-[623px] w-full max-w-[1007px] p-[16px] text-gray-900">
      <div className="relative flex h-full w-full flex-col items-center justify-center rounded-[16px] bg-[#fff] p-[16px]">
        {!prefersReducedMotion ? <Confetti /> : null}
        <SuccessMessage subscriptionType={subscriptionType} onClose={onClose} />
      </div>
    </div>
  )
}

export function SuccessMessage({
  onClose,
  subscriptionType,
}: {
  onClose: () => void
  subscriptionType: 'all-access' | 'studio-ai'
}) {
  const [langCookie] = useLangCookie()
  const locale = langCookie || defaultLanguage
  const t = i18n[locale]

  const initial = { y: 12, opacity: 0 }
  const animate = { y: 0, opacity: 1 }
  const [clicked, setClicked] = React.useState(false)

  return (
    <AnimatePresence>
      <motion.h4
        key="h4"
        initial={initial}
        animate={animate}
        transition={{ delay: 0.32 }}
        className="text-[18px] font-[700] leading-[23.10px] text-[#6B7280] lg:text-[24px]"
      >
        {t.welcome}
      </motion.h4>
      <motion.h2
        key="h2"
        initial={initial}
        animate={animate}
        transition={{ delay: 0.41 }}
        className="payments-animated-header text-[48px] font-[700] text-[#2A1F3F] lg:text-[72px]"
      >
        {subscriptionType === 'all-access' ? t.all_access : t.ai_studio}
      </motion.h2>
      <motion.div
        key="div"
        initial={initial}
        animate={animate}
        transition={{ delay: 0.5 }}
        className="flex w-full justify-center"
      >
        <Button
          type="button"
          onClick={callAll(onClose, () => setClicked(true))}
          className="mt-[20px] w-full grow sm:max-w-[376px]"
        >
          {clicked ? <AnimatedSpinnerIcon /> : t.get_started}
        </Button>
      </motion.div>
    </AnimatePresence>
  )
}
